import React, { useCallback, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import {
  Tabs,
  Tab,
  CardContent,
  Card,
  LinearProgress,
  Box,
  Button,
} from '@mui/material';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';

import { Passenger } from '@/data/models/passenger.model';
import { ServicePlan } from '@/data/models/service-plan.model';
import {
  fetchPassengerFutureReservationsAsync,
  fetchPassengerPastReservationsAsync,
  loadMorePassengerFutureReservationsAsync,
  loadMorePassengerPastReservationsAsync,
  selectCurrentPassengerFutureReservations,
  selectCurrentPassengerPastReservations,
  selectLoadingCurrentPassengerFutureReservations,
  selectLoadingCurrentPassengerPastReservations,
} from '@/data/passenger.slice';

import TabPanel from '../../TabPanel';
import ReservationList from './ReservationList';
import { AppDispatch } from '@/data/store';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { selectReservationEventEmitted } from '@/data/reservation.slice';
import {
  selectTripLegEventEmitted,
  setTripLegEventEmitted,
} from '@/data/tripleg.slice';

const PREFIX = 'PassengerReservation';

const classes = {
  container: `${PREFIX}-container`,
  loadMoreContainer: `${PREFIX}-loadMoreContainer`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: '100%',
  },
  [`& .${classes.loadMoreContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

interface PassengerReservationProps {
  passengerUUID: string;
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
  loadingPassenger: boolean;
}

export default function PassengerReservations({
  passengerUUID,
  passenger,
  servicePlans,
  loadingPassenger,
}: PassengerReservationProps) {
  const dispatch: AppDispatch = useAppDispatch();
  const t = useCommonTranslation();

  const [tabIndex, setTabIndex] = React.useState(0);

  const reservationEventDetail = useAppSelector(selectReservationEventEmitted);
  const tripLegEventDetail = useAppSelector(selectTripLegEventEmitted);
  const pastReservationsLoading = useAppSelector(
    selectLoadingCurrentPassengerPastReservations,
  );
  const pastReservations = useAppSelector(
    selectCurrentPassengerPastReservations,
  );
  const futureReservationsLoading = useAppSelector(
    selectLoadingCurrentPassengerFutureReservations,
  );
  const futureReservations = useAppSelector(
    selectCurrentPassengerFutureReservations,
  );

  const fetchPassengerFutureReservations = useCallback(() => {
    if (passengerUUID) {
      dispatch(fetchPassengerFutureReservationsAsync(passengerUUID, 100));
    }
  }, [passengerUUID]);

  const fetchPassengerPastReservations = useCallback(() => {
    if (passengerUUID) {
      dispatch(fetchPassengerPastReservationsAsync(passengerUUID, 100));
    }
  }, [passengerUUID]);

  const loadMorePastReservations = useCallback(() => {
    if (passengerUUID && pastReservations.continuationToken) {
      dispatch(
        loadMorePassengerPastReservationsAsync(
          passengerUUID,
          100,
          pastReservations.continuationToken,
        ),
      );
    }
  }, [passengerUUID, pastReservations.continuationToken]);

  const loadMoreFutureReservations = useCallback(() => {
    if (passengerUUID && futureReservations.continuationToken) {
      dispatch(
        loadMorePassengerFutureReservationsAsync(
          passengerUUID,
          100,
          futureReservations.continuationToken,
        ),
      );
    }
  }, [passengerUUID, futureReservations.continuationToken]);

  // initially only fetch future reservations; guard against the
  const fetchedForUUID = useRef<string | null>(null);
  useEffect(() => {
    if (passengerUUID && fetchedForUUID.current !== passengerUUID) {
      fetchedForUUID.current = passengerUUID;
      fetchPassengerFutureReservations();
    }
  }, [passengerUUID]);

  useEffect(() => {
    if (
      passengerUUID &&
      reservationEventDetail?.detail?.passengerUUID === passengerUUID
    ) {
      fetchPassengerFutureReservations();
      fetchPassengerPastReservations();
    }
  }, [
    reservationEventDetail,
    fetchPassengerFutureReservations,
    fetchPassengerPastReservations,
  ]);

  useEffect(() => {
    if (
      passengerUUID &&
      tripLegEventDetail?.detail.passengerUUID === passengerUUID
    ) {
      fetchPassengerFutureReservations();
      fetchPassengerPastReservations();
      dispatch(setTripLegEventEmitted(null));
    }
  }, [tripLegEventDetail]);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    // load only past reservations if tab is changed to history and they are not loaded
    if (
      newValue === 1 &&
      !pastReservationsLoading &&
      !pastReservations.reservations?.length
    ) {
      fetchPassengerPastReservations();
    }

    setTabIndex(newValue);
  };

  return (
    <StyledCard className={classes.container}>
      <CardContent>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={tabIndex}
          onChange={handleTabChange}
          variant="standard">
          <Tab label={t('passenger.upcoming')} data-testid="tab-one" />
          <Tab label={t('passenger.history')} data-testid="tab-two" />
        </Tabs>
        <TabPanel value={tabIndex} index={0}>
          {futureReservationsLoading && <LinearProgress />}
          <ReservationList
            passenger={passenger}
            reservations={futureReservations}
            servicePlans={servicePlans}
            loadingPassenger={loadingPassenger}
            data-testid="tab-panel-one"
          />
          {futureReservations.continuationToken && (
            <Box className={classes.loadMoreContainer}>
              <Button
                variant="outlined"
                color="primary"
                disabled={futureReservationsLoading}
                onClick={loadMoreFutureReservations}>
                {t('common.loadMore')}
              </Button>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          {pastReservationsLoading && <LinearProgress />}
          <ReservationList
            passenger={passenger}
            reservations={pastReservations}
            servicePlans={servicePlans}
            loadingPassenger={loadingPassenger}
            data-testid="tab-panel-two"
          />
          {pastReservations.continuationToken && (
            <Box className={classes.loadMoreContainer}>
              <Button
                variant="outlined"
                color="primary"
                disabled={pastReservationsLoading}
                onClick={loadMorePastReservations}>
                {t('common.loadMore')}
              </Button>
            </Box>
          )}
        </TabPanel>
      </CardContent>
    </StyledCard>
  );
}
